import type { Article } from "./types";

export const article: Article = {
  slug: "digital-sabbath",
  title: "A digital sabbath that lasts past the second week",
  h1: "How to keep a digital sabbath",
  description:
    "One day a week without the feed sounds simple and collapses fast. The four decisions that make it survivable, and why the day needs a plan of its own.",
  targets: [
    "digital sabbath",
    "technology sabbath rules",
    "one day a week without phone",
    "screen free sunday",
  ],
  published: "2026-08-17",
  standfirst:
    "The first digital sabbath feels like a relief. The third feels like an afternoon you do not know what to do with.",
  takeaways: [
    "A digital sabbath fails on the third or fourth week, when the novelty is gone and the day has nothing in it — so the day needs a plan before the rules need enforcing.",
    "Define it by what is off, not by what is allowed: two or three named apps, for one fixed span, rather than a vague sense of using the phone less.",
    "Prepare on the day before — tickets, addresses, recipes, directions — because the most common breach is a genuine need for information rather than a craving.",
    "Rest is the point, not abstinence. A sabbath measured by how strictly it was kept has already become the kind of work it was meant to interrupt.",
  ],
  body: [
    {
      type: "p",
      text: "The idea is old and the modern version is about fifteen years old: one day a week, or one evening, without the feeds. Almost everyone who tries it reports the same arc. The first one is a relief. The second is pleasant. Somewhere around the third or fourth the day arrives with nothing in it, the phone is right there, and the practice ends without a decision.",
    },
    {
      type: "p",
      text: "Which points at the real problem. A digital sabbath is not difficult to start and it is not difficult to keep on willpower. It is difficult to keep because an empty day is unpleasant, and the thing that fills empty days is the thing you removed.",
    },
    {
      type: "h2",
      id: "content",
      text: "The day needs content before it needs rules",
    },
    {
      type: "p",
      text: "The traditional sabbath was never an empty day. It was a full one — a different fullness from the other six, but full: worship, a long meal, people, walking, sleeping in the afternoon without apology. Subtraction was the shape of it, not the substance.",
    },
    {
      type: "p",
      text: "So decide what the day is for before deciding what is off it. Concretely: who you will see, what you will cook, where you will walk, what you will read. A digital sabbath with a plan is a day people look forward to; one without is an endurance test, and endurance tests have a natural lifespan of about a month.",
    },
    {
      type: "note",
      text: 'If the honest answer to "what will I do instead" is "be bored, on purpose", the practice will not reach week five.',
    },
    {
      type: "h2",
      id: "define",
      text: "Define it narrowly, in writing",
    },
    {
      type: "p",
      text: '"No phone on Sundays" is unkeepable and everyone discovers this within one Sunday — you will need a map, a recipe, a photograph, a call. The rule then bends, and once bent nobody remembers where it originally stood.',
    },
    {
      type: "p",
      text: "Name what is off instead:",
    },
    {
      type: "ol",
      items: [
        'The two or three apps that actually take your attention. Not "the internet" — the specific ones you open without deciding to.',
        'A fixed span with a start and an end. Saturday evening to Sunday evening, or simply Sunday until dinner. An open-ended "as long as I can" is not a sabbath, it is a test you eventually fail.',
        "Calls and messages stay on. A practice that could make you miss something real is one you will abandon entirely at the first scare.",
        "Work email is off. This is usually the hardest line and the one that carries most of the benefit.",
      ],
    },
    {
      type: "h2",
      id: "prepare",
      text: "Prepare the day before",
    },
    {
      type: "p",
      text: "The most common breach is not craving. It is information — the address, the opening hours, the recipe, the train time. You reach for the phone with an entirely legitimate purpose, and thirty minutes later you are somewhere else entirely.",
    },
    {
      type: "p",
      text: "So do on Saturday what Saturday is for: look things up, write them down, download the map. It takes ten minutes and it removes most of the breaches, because it removes the honest reasons for them.",
    },
    {
      type: "p",
      text: "For the rest, a block with a real cost is what holds — the same property that separates {a-block-that-survives-from-a-reminder} on any other day of the week. A rule you can revoke with one tap is not a rule on Sunday either.",
      links: {
        "a-block-that-survives-from-a-reminder":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "h2",
      id: "rest",
      text: "Rest is the point, not abstinence",
    },
    {
      type: "p",
      text: "There is a failure mode particular to religious people attempting this, and it is worth naming: the sabbath becomes a performance with a score. Did I keep it properly. Did I check something I should not have. Was that a breach.",
    },
    {
      type: "p",
      text: "A day measured that way has become the same thing it was meant to interrupt — an obligation with a metric attached. If you check something, you checked something. The day is not void.",
    },
    {
      type: "verse",
      verse: {
        text: "The Sabbath was made for man, not man for the Sabbath.",
        ref: "Mark 2:27",
      },
    },
    {
      type: "p",
      text: "That verse is aimed precisely at people constructing careful rules around a day of rest. It is not permission to abandon the practice; it is a reminder of which one exists for the other.",
    },
    {
      type: "h2",
      id: "start",
      text: "If you want to start this week",
    },
    {
      type: "ol",
      items: [
        "Pick the span. Start with an evening rather than a whole day — Saturday after dinner to Sunday lunch is a real sabbath and a survivable first one.",
        "Name two apps. Write them down.",
        "Decide the one thing the span is FOR. A meal with someone, a walk, a book, church without a phone in your pocket.",
        "Prepare the information the night before.",
        "Keep it for four weeks before judging it. The third is the hard one, and the third is also the one that tells you whether the day has content.",
      ],
    },
    {
      type: "p",
      text: "{Bible-Shield} can hold the span for you — the apps you name close for the hours you choose and open again after a short prayer, which is a more useful boundary than one you have to keep re-deciding all afternoon. If a whole day is too much to begin with, {the-evening-version} is the smaller practice underneath it.",
      links: {
        "Bible-Shield": "/",
        "the-evening-version": "/blog/how-to-stop-scrolling-at-night",
      },
    },
    {
      type: "p",
      text: "For the scripture behind this rather than the practice, {ten-passages-on-rest} sets out what the fourth commandment actually asks and who it was written to protect — which is a shorter and more uncomfortable list than most people expect.",
      links: { "ten-passages-on-rest": "/verses/bible-verses-about-rest" },
    },
  ],
  faq: [
    {
      q: "What is a digital sabbath?",
      a: "A fixed, recurring span — usually a day or an evening each week — with the apps that take your attention switched off, kept for rest rather than for discipline. The modern name is recent; the practice it borrows from is not.",
    },
    {
      q: "Does a digital sabbath have to be on Sunday?",
      a: "No. Pick the day your life actually has room in. For people who work weekends, a Tuesday sabbath is a real one, and a Sunday attempt that never survives contact with a shift pattern is not.",
    },
    {
      q: "What about emergencies?",
      a: "Calls, messages and maps stay on, always. Block the feeds, never the phone — a practice that could make you miss something genuine is one you will drop entirely at the first scare, and then you have nothing.",
    },
    {
      q: "Why does it get harder after a few weeks?",
      a: "Because the novelty carried the first two and the day itself has to carry the rest. That is the signal to give the day content — people, a meal, a walk, something planned — rather than to tighten the rules.",
    },
  ],
  related: ["social-media-fast-guide", "how-to-stop-scrolling-at-night"],
};
